import {
  ADD_DEPLOYMENT_ENVIRONMENT,
  ADD_DEPLOYMENT_ENVIRONMENT_IS_ERROR,
  ADD_DEPLOYMENT_ENVIRONMENT_IS_PENDING,
  CANCEL_EDITION,
  CLEAN_ERROR,
  DeploymentEnvironmentActions,
  DeploymentEnvironmentReducerState,
  FETCH_APPLICATION_BUILD_BY_DEPLOYMENT_ENVIRONMENT,
  FETCH_APPLICATION_BUILD_BY_DEPLOYMENT_ENVIRONMENT_IS_ERROR,
  FETCH_APPLICATION_BUILD_BY_DEPLOYMENT_ENVIRONMENT_IS_PENDING,
  FETCH_APPLICATION_BY_DEPLOYMENT_ENVIRONMENT,
  FETCH_APPLICATION_BY_DEPLOYMENT_ENVIRONMENT_IS_ERROR,
  FETCH_APPLICATION_BY_DEPLOYMENT_ENVIRONMENT_IS_PENDING,
  FETCH_DEPLOYMENT_ENVIRONMENT,
  FETCH_DEPLOYMENT_ENVIRONMENT_IS_ERROR,
  FETCH_DEPLOYMENT_ENVIRONMENT_IS_PENDING,
  FETCH_FLEETS_BY_APPLICATION_AND_DEPLOYMENT_ENVIRONMENT,
  FETCH_FLEETS_BY_APPLICATION_AND_DEPLOYMENT_ENVIRONMENT_IS_ERROR,
  FETCH_FLEETS_BY_APPLICATION_AND_DEPLOYMENT_ENVIRONMENT_IS_PENDING,
  REMOVE_DEPLOYMENT_ENVIRONMENT,
  REMOVE_DEPLOYMENT_ENVIRONMENT_IS_ERROR,
  REMOVE_DEPLOYMENT_ENVIRONMENT_IS_PENDING,
  SELECT_DEPLOYMENT_ENVIRONMENT,
  SORT,
  UPDATE_DEPLOYMENT_ENVIRONMENT,
  UPDATE_DEPLOYMENT_ENVIRONMENT_IS_ERROR,
  UPDATE_DEPLOYMENT_ENVIRONMENT_IS_PENDING,
} from 'services/GameHosting/DeploymentEnv/types';
import { addNew, sort } from 'utils/functions';

const INITIAL_STATE = {
  depEnv: [],
  sortBy: 'name',
  sortDirection: 'ASC',
  selectedDeploymentEnvironmentId: null,

  fetchIsPending: false,
  fetchIsCompleted: false,
  fetchIsError: null,

  addIsPending: false,
  addIsCompleted: false,
  addIsError: null,

  editIsPending: false,
  editIsCompleted: false,
  editIsError: null,

  deleteIsPending: false,
  deleteIsCompleted: false,
  deleteIsError: null,

  application: [],
  fetchApplicationIsPending: false,
  fetchApplicationIsCompleted: false,
  fetchApplicationIsError: null,

  applicationBuild: [],
  fetchApplicationBuildIsPending: false,
  fetchApplicationBuildIsCompleted: false,
  fetchApplicationBuildIsError: null,

  fleet: [],
  fetchFleetIsPending: false,
  fetchFleetIsCompleted: false,
  fetchFleetIsError: null,
} satisfies DeploymentEnvironmentReducerState;

const deploymentEnvironmentReducer = (
  previousState: DeploymentEnvironmentReducerState | undefined,
  action: DeploymentEnvironmentActions,
): DeploymentEnvironmentReducerState => {
  const state: DeploymentEnvironmentReducerState = previousState ?? INITIAL_STATE;

  switch (action.type) {
    case FETCH_DEPLOYMENT_ENVIRONMENT:
      return {
        ...state,
        depEnv: sort(action.data ?? [], {
          sortBy: state.sortBy,
          sortDirection: state.sortDirection,
        }),
        fetchIsCompleted: true,
        fetchIsPending: false,
        fetchIsError: null,
      };
    case FETCH_DEPLOYMENT_ENVIRONMENT_IS_PENDING:
      return {
        ...state,
        fetchIsPending: true,
        fetchIsCompleted: false,
        fetchIsError: null,
      };
    case FETCH_DEPLOYMENT_ENVIRONMENT_IS_ERROR:
      return {
        ...state,
        fetchIsCompleted: true,
        fetchIsPending: false,
        fetchIsError: action.error,
      };
    case SELECT_DEPLOYMENT_ENVIRONMENT:
      return {
        ...state,
        selectedDeploymentEnvironmentId: action.depEnvId,
      };
    case CANCEL_EDITION:
      return {
        ...state,
        depEnv: state.depEnv.filter((env) => env.id),
      };

    case ADD_DEPLOYMENT_ENVIRONMENT: {
      if (!action.data) {
        return {
          ...state,
          addIsCompleted: true,
          addIsPending: false,
          addIsError: new Error('No data to add'),
        };
      }

      return {
        ...state,
        depEnv: addNew(action.data, state.depEnv),
        addIsCompleted: true,
        addIsPending: false,
        addIsError: null,
      };
    }

    case ADD_DEPLOYMENT_ENVIRONMENT_IS_PENDING:
      return {
        ...state,
        addIsCompleted: false,
        addIsPending: true,
        addIsError: null,
      };
    case ADD_DEPLOYMENT_ENVIRONMENT_IS_ERROR:
      return {
        ...state,
        addIsCompleted: true,
        addIsPending: false,
        addIsError: action.error,
      };

    case UPDATE_DEPLOYMENT_ENVIRONMENT: {
      const update = action.data;

      if (!update) {
        // nothing to update
        return { ...state, editIsPending: false, editIsCompleted: true, editIsError: null };
      }

      return {
        ...state,
        depEnv: state.depEnv.map((depenv) => {
          if (`${depenv.id}` === `${update.id}`) {
            return {
              ...depenv,
              ...action.data,
            };
          }

          return depenv;
        }),
        editIsPending: false,
        editIsCompleted: true,
        editIsError: null,
      };
    }

    case UPDATE_DEPLOYMENT_ENVIRONMENT_IS_PENDING:
      return {
        ...state,
        editIsPending: true,
        editIsCompleted: false,
        editIsError: null,
      };
    case UPDATE_DEPLOYMENT_ENVIRONMENT_IS_ERROR:
      return {
        ...state,
        editIsPending: false,
        editIsCompleted: true,
        editIsError: action.error,
      };

    case REMOVE_DEPLOYMENT_ENVIRONMENT: {
      const toRemove = action.data;

      if (!toRemove) {
        return { ...state, deleteIsPending: false, deleteIsCompleted: true, deleteIsError: null };
      }

      const update = state.depEnv.filter((depenv) => `${depenv.id}` !== `${toRemove.id}`);

      return {
        ...state,
        depEnv: update,
        selectedDeploymentEnvironmentId:
          `${state.selectedDeploymentEnvironmentId}` === `${toRemove.id}` ? update[0]?.id ?? null : state.selectedDeploymentEnvironmentId,
        deleteIsPending: false,
        deleteIsCompleted: true,
        deleteIsError: null,
      };
    }

    case REMOVE_DEPLOYMENT_ENVIRONMENT_IS_PENDING:
      return {
        ...state,
        deleteIsPending: true,
        deleteIsCompleted: false,
        deleteIsError: null,
      };
    case REMOVE_DEPLOYMENT_ENVIRONMENT_IS_ERROR:
      return {
        ...state,
        deleteIsPending: false,
        deleteIsCompleted: true,
        deleteIsError: action.error,
      };

    case FETCH_APPLICATION_BY_DEPLOYMENT_ENVIRONMENT:
      return {
        ...state,
        application: action.data ?? [],
        fetchApplicationIsPending: false,
        fetchApplicationIsCompleted: true,
        fetchApplicationIsError: null,
      };
    case FETCH_APPLICATION_BY_DEPLOYMENT_ENVIRONMENT_IS_PENDING:
      return {
        ...state,
        fetchApplicationIsPending: true,
        fetchApplicationIsCompleted: false,
        fetchApplicationIsError: null,
      };
    case FETCH_APPLICATION_BY_DEPLOYMENT_ENVIRONMENT_IS_ERROR:
      return {
        ...state,
        fetchApplicationIsPending: false,
        fetchApplicationIsCompleted: true,
        fetchApplicationIsError: action.error,
      };

    case FETCH_APPLICATION_BUILD_BY_DEPLOYMENT_ENVIRONMENT:
      return {
        ...state,
        applicationBuild: action.data ?? [],
        fetchApplicationBuildIsPending: false,
        fetchApplicationBuildIsCompleted: true,
        fetchApplicationBuildIsError: null,
      };
    case FETCH_APPLICATION_BUILD_BY_DEPLOYMENT_ENVIRONMENT_IS_PENDING:
      return {
        ...state,
        fetchApplicationBuildIsPending: true,
        fetchApplicationBuildIsCompleted: false,
        fetchApplicationBuildIsError: null,
      };
    case FETCH_APPLICATION_BUILD_BY_DEPLOYMENT_ENVIRONMENT_IS_ERROR:
      return {
        ...state,
        fetchApplicationBuildIsPending: false,
        fetchApplicationBuildIsCompleted: true,
        fetchApplicationBuildIsError: action.error,
      };

    case FETCH_FLEETS_BY_APPLICATION_AND_DEPLOYMENT_ENVIRONMENT:
      return {
        ...state,
        fleet: action.data ?? [],
        fetchFleetIsPending: false,
        fetchFleetIsCompleted: true,
        fetchFleetIsError: null,
      };
    case FETCH_FLEETS_BY_APPLICATION_AND_DEPLOYMENT_ENVIRONMENT_IS_PENDING:
      return {
        ...state,
        fetchFleetIsPending: true,
        fetchFleetIsCompleted: false,
        fetchFleetIsError: null,
      };
    case FETCH_FLEETS_BY_APPLICATION_AND_DEPLOYMENT_ENVIRONMENT_IS_ERROR:
      return {
        ...state,
        fetchFleetIsPending: false,
        fetchFleetIsCompleted: true,
        fetchFleetIsError: action.error,
      };

    case SORT:
      return {
        ...state,
        sortBy: action.params.sortSearchParam,
        sortDirection: state.sortDirection === 'ASC' ? 'DESC' : 'ASC',
        depEnv: sort(state.depEnv, {
          sortBy: action.params.sortSearchParam,
          sortDirection: state.sortDirection === 'ASC' ? 'DESC' : 'ASC',
          sortSearchValue: action.params.sortSearchValue || action.params.value,
        }),
      };
    case CLEAN_ERROR:
      return {
        ...state,
        addIsError: null,
        editIsError: null,
        deleteIsError: null,
      };

    case 'RESET_DEPLOYMENT_ENV_REDUCER':
    case 'RESET_REDUCER':
      return INITIAL_STATE;

    default:
      return state;
  }
};

export default deploymentEnvironmentReducer;
